import classNames from "classnames";
import DOMPurify from "dompurify";
import capitalize from "lodash/capitalize";
import {useEffect} from "react";
import {useFormContext} from "react-hook-form";
import {FiEdit2, FiTrash} from "react-icons/fi";

import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {IconButton} from "@/components/iconButton/IconButton.tsx";
import {IncidentState} from "@/lib/types/statuspages.ts";
import {IncidentStateIcon} from "@/pages/incidents/components/incident-edit/components/updates/IncidentStateSelect/IncidentStateIcon.tsx";
import {AffectedComponent} from "@/pages/incidents/components/incident-edit/IncidentForm.types.ts";
import {isAttachedComponent} from "@/pages/incidents/components/incident-edit/IncidentForm.utils.tsx";

import styles from "./UpdateFieldPreview.module.scss";

interface UpdateFieldPreview {
  name: string;
  descriptionHTML: string;
  updatedAt?: string;
  variant: "standalone" | "history";
  onEditClick: () => void;
  onDeleteClick: () => void;
  updateRequired: boolean;
}

export const UpdateFieldPreview = ({
  name,
  descriptionHTML,
  updatedAt,
  onEditClick,
  onDeleteClick,
  updateRequired,
  variant,
}: UpdateFieldPreview) => {
  const {watch, setValue} = useFormContext();
  const state = watch(`${name}.incident_state`);
  const notifyVisible = !watch(`${name}.id`);
  const affectedComponents = watch("affected_components") as AffectedComponent[] | undefined;
  // nothing to reset unless this update resolves the incident and a component is attached to it
  const resolveComponentsVisible = state === IncidentState.RESOLVED && !!affectedComponents?.some(isAttachedComponent);
  useEffect(() => {
    if (notifyVisible) {
      setValue("notify_subscribers", notifyVisible);
    }
  }, [setValue, notifyVisible]);
  useEffect(() => {
    // only the unsaved update owns this form-wide field, and it must stay off while hidden -
    // the backend rejects it without an end time
    if (notifyVisible) {
      setValue("resolve_components", resolveComponentsVisible);
    }
  }, [setValue, notifyVisible, resolveComponentsVisible]);
  return (
    <div
      className={classNames("d-flex flex-row", styles.container, {
        [styles.historyVariant]: variant === "history",
        [styles.standaloneVariant]: variant === "standalone",
      })}
    >
      <div className={styles.verticalLine}>
        <div className={styles.topLine} />
        <div className={styles.iconSection}>
          <IncidentStateIcon state={state} className={styles.incidentIcon} />
        </div>
        <div className={styles.bottomLine} />
      </div>
      <div className={classNames("card pulse-bg-gray-50 w-100", {["mb-3"]: variant === "history"})}>
        <div className={classNames("card-body", styles.cardBody)}>
          <div className={styles.iconColumn}>
            <IncidentStateIcon state={state} type={"checks"} />
          </div>
          <div className={styles.stateColumn}>
            <span className="pulseTextBodySmallBold">{capitalize(state)}</span>
            <span className="ms-2 pulseTextBodySmall pulse-color-gray-600">{updatedAt}</span>
          </div>
          <div className={styles.actionsColumn}>
            <IconButton size={"fitIcon"} variant="ghost" icon={FiEdit2} onClick={onEditClick} />
            {!updateRequired && <IconButton size={"fitIcon"} variant="ghost" icon={FiTrash} onClick={onDeleteClick} />}
          </div>
          {descriptionHTML && (
            <div className={classNames("pulseTextBodySmall", styles.descriptionRow)}>
              <div dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(descriptionHTML)}} />
            </div>
          )}
          {notifyVisible && (
            <div className={styles.checkboxRow}>
              <SwitchField
                name={"notify_subscribers"}
                label={"Send notifications to Status Page subscribers"}
                tooltip={"Sends notifications to all enabled subscription methods about this update."}
              />
            </div>
          )}
          {notifyVisible && resolveComponentsVisible && (
            <div className={styles.checkboxRow}>
              <SwitchField name={"resolve_components"} label={'Reset all affected components to "Operational"'} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
